// Razorpay Service
// Purpose: Handle Razorpay payment gateway integration

import { PRICE, getCurrentPricing, subscribeToPricing } from "../constants/pricing";

const RAZORPAY_KEY_ID = import.meta.env?.VITE_RAZORPAY_KEY_ID;

// Helper function to calculate price with GST
const calculatePriceWithGST = (price, gstPercentage) => {
  if (!gstPercentage || gstPercentage <= 0) return price;
  return price + (price * gstPercentage / 100);
};

// Helper function to get current pricing with GST
const getCurrentPricingWithGST = (currency = 'USD') => {
  const currentPricing = getCurrentPricing();
  const paidData = currentPricing.paid;
  const currencyData = paidData[currency];
  
  if (currency === 'INR') {
    const basePrice = currencyData.offer;
    const gstPercentage = currencyData.gst;
    
    if (gstPercentage && gstPercentage > 0) {
      const finalPrice = calculatePriceWithGST(basePrice, gstPercentage);
      return {
        amount: finalPrice * 100, // Convert to paise/cents
        currency: 'INR',
        display: `₹${finalPrice.toLocaleString('en-IN')}`,
        basePrice: basePrice,
        gstPercentage: gstPercentage,
        gstAmount: finalPrice - basePrice
      };
    }
    
    return {
      amount: basePrice * 100,
      currency: 'INR',
      display: `₹${basePrice.toLocaleString('en-IN')}`,
      basePrice: basePrice,
      gstPercentage: null,
      gstAmount: 0
    };
  } else {
    const basePrice = currencyData.offer;
    
    return {
      amount: basePrice * 100,
      currency: 'USD',
      display: `$${basePrice}`,
      basePrice: basePrice,
      taxPercentage: null,
      taxAmount: 0
    };
  }
};

// Dynamic pricing object that updates in real-time
let currentDynamicPricing = {
  USD: getCurrentPricingWithGST('USD'),
  INR: getCurrentPricingWithGST('INR')
};

// Subscribe to pricing updates
subscribeToPricing(() => {
  console.log('Razorpay service: Pricing updated, refreshing...');
  currentDynamicPricing = {
    USD: getCurrentPricingWithGST('USD'),
    INR: getCurrentPricingWithGST('INR')
  };
});

const debugLog = (step, data) => {
  console.log(`[Razorpay Debug - ${step}]`, {
    timestamp: new Date()?.toISOString(),
    step,
    data
  });
};

const debugError = (step, error) => {
  console.error(`[Razorpay Error - ${step}]`, {
    timestamp: new Date()?.toISOString(),
    step,
    error: {
      message: error?.message,
      code: error?.code,
      description: error?.description,
      stack: error?.stack
    }
  });
};

const validateConfig = () => {
  debugLog('Config Validation', {
    hasKeyId: !!RAZORPAY_KEY_ID,
    keyIdLength: RAZORPAY_KEY_ID?.length,
    keyIdPrefix: RAZORPAY_KEY_ID?.substring(0, 10)
  });
  if (!RAZORPAY_KEY_ID || RAZORPAY_KEY_ID === 'your-razorpay-key-id-here') {
    debugError('Config Validation', new Error('Razorpay Key ID not configured'));
    return false;
  }
  return true;
};

export const razorpayService = {
  getPricing(currency = 'USD') {
    const pricing = currentDynamicPricing?.[currency] || currentDynamicPricing?.USD;
    debugLog('Get Pricing', { currency, pricing });
    return pricing;
  },

  getPricingWithBreakdown(currency = 'USD') {
    const pricing = this.getPricing(currency);

    return {
      ...pricing,

      base_price: pricing.basePrice,
      tax_percentage: pricing.gstPercentage || pricing.taxPercentage || 0,
      tax_amount: pricing.gstAmount || pricing.taxAmount || 0,
      total_amount: pricing.amount / 100,

      is_gst_applicable: currency === 'INR' && pricing.gstPercentage > 0
    };
  },

  async detectCurrency() {
    try {
      const timezone = Intl.DateTimeFormat()?.resolvedOptions()?.timeZone;
      debugLog('Currency Detection', { timezone });
      if (timezone?.includes('Asia/Kolkata') || timezone?.includes('Asia/Calcutta')) {
        return 'INR';
      }
      return 'USD';
    } catch (error) {
      debugError('Currency Detection', error);
      return 'USD';
    }
  },

  loadRazorpayScript() {
    return new Promise((resolve, reject) => {
      if (window.Razorpay) { resolve(true); return; }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = (error) => {
        debugError('Script Loading', error);
        reject(new Error('Failed to load Razorpay SDK. Please check your internet connection.'));
      };
      document.body.appendChild(script);
    });
  },

  validatePaymentConfig() {
    if (!RAZORPAY_KEY_ID || RAZORPAY_KEY_ID === 'your-razorpay-key-id-here') {
      const error = new Error('Razorpay Key ID not configured. Please add your Razorpay test key to the .env file.');
      debugError('Payment Config Validation', error);
      throw error;
    }
    return true;
  },

  async createOrder(amount, currency, userEmail, userName) {
    try {
      const order = { amount, currency, receipt: `receipt_${Date.now()}` };
      debugLog('Order Creation Result', order);
      return order;
    } catch (error) {
      debugError('Order Creation', error);
      throw new Error('Failed to create payment order');
    }
  },

  async openCheckout({
    orderId,
    amount,
    currency,
    userEmail,
    userName,
    userPhone,
    onSuccess,
    onFailure,
    onCancel,
  }) {
    try {
      // Get fresh pricing before opening checkout
      const pricing = this.getPricing(currency);
      const finalAmount = amount || pricing.amount;
      const finalCurrency = currency || pricing.currency;
      
      debugLog('Checkout Initialization', { 
        orderId, 
        amount: finalAmount, 
        currency: finalCurrency, 
        userEmail,
        pricing,
        breakdown: {
          basePrice: pricing.basePrice,
          gstPercentage: pricing.gstPercentage || pricing.taxPercentage,
          gstAmount: pricing.gstAmount || pricing.taxAmount,
          total: finalAmount / 100
        }
      });

      this.validatePaymentConfig();
      await this.loadRazorpayScript();

      if (!window.Razorpay) throw new Error('Razorpay SDK not loaded properly');

      // ── Race condition flags ───────────────────────────────────────────────
      let paymentFailedFired = false;
      let userCancelled      = false;

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: finalAmount,
        currency: finalCurrency,
        name: 'Decx AI',
        description: `Paid Deck Audit - AI + Human Powered${pricing.gstPercentage ? ` (includes ${pricing.gstPercentage}% GST)` : ''}`,
        prefill: {
          email:   userEmail,
          name:    userName,
          contact: userPhone || ''
        },
        theme: { color: '#004AAD' },

        handler: function (response) {
          if (userCancelled) {
            debugLog('SUCCESS BLOCKED — user cancelled before capture completed', {
              razorpay_payment_id: response?.razorpay_payment_id,
            });
            console.warn(
              '[MyDeckAudit] UPI payment captured after cancel. Needs manual review. Payment ID:',
              response?.razorpay_payment_id
            );
            return;
          }

          debugLog('Payment Success Handler', {
            razorpay_payment_id: response?.razorpay_payment_id,
            razorpay_order_id:   response?.razorpay_order_id,
            razorpay_signature:  response?.razorpay_signature
          });
          onSuccess({
            razorpay_payment_id: response?.razorpay_payment_id,
            razorpay_order_id:   response?.razorpay_order_id   || null,
            razorpay_signature:  response?.razorpay_signature  || null,
            pricing_details: {
              basePrice: pricing.basePrice,
              taxAmount: pricing.gstAmount || pricing.taxAmount,
              taxPercentage: pricing.gstPercentage || pricing.taxPercentage,
              total: finalAmount / 100
            }
          });
        },

        modal: {
          ondismiss: function () {
            if (paymentFailedFired) {
              debugLog('Modal Dismissed', { reason: 'After payment.failed — ignoring' });
              return;
            }
            userCancelled = true;
            debugLog('Modal Dismissed', { reason: 'User cancelled' });
            if (onCancel) {
              onCancel({
                code:        'payment_cancelled',
                reason:      'payment_cancelled',
                description: 'Payment was cancelled by user',
              });
            } else {
              onFailure({
                code:        'payment_cancelled',
                reason:      'payment_cancelled',
                description: 'Payment was cancelled by user',
              });
            }
          }
        }
      };

      const razorpay = new window.Razorpay(options);

      razorpay?.on('payment.failed', function (response) {
        paymentFailedFired = true;
        debugError('Payment Failed Event', {
          code:        response?.error?.code,
          description: response?.error?.description,
          reason:      response?.error?.reason,
          source:      response?.error?.source,
          step:        response?.error?.step,
          metadata:    response?.error?.metadata
        });
        onFailure({
          code:        response?.error?.code,
          description: response?.error?.description,
          reason:      response?.error?.reason,
        });
      });

      debugLog('Checkout Flow', { step: 'Opening Razorpay modal' });
      razorpay?.open();

    } catch (error) {
      debugError('Checkout Error', error);
      let errorMessage = 'Failed to open payment checkout';
      if (error?.message?.includes('Razorpay Key ID not configured')) {
        errorMessage = 'Payment gateway not configured properly';
      } else if (error?.message?.includes('Failed to load Razorpay SDK')) {
        errorMessage = 'Failed to load payment gateway. Please check your internet connection.';
      } else if (error?.message) {
        errorMessage = error?.message;
      }
      onFailure({ code: 'checkout_error', description: errorMessage });
      throw error;
    }
  }
};

validateConfig();

export default razorpayService;